import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { GetClasses } from '@lifeomic/chroma-react/typeUtils';
import { Fade } from '@mui/material';
import useChainedTransition from '../../../../../hooks/useChainedTransition';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { SingleFhirSearchResult } from '../../../hooks/SearchService';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Pill } from '@lifeomic/chroma-react/components/Pill';

const messages = defineMessages({
  loadingData: {
    id: 'resultsMessage.loadingData',
    defaultMessage: 'Loading more data...',
  },
  loadMore: {
    id: 'resultsMessage.loadMore',
    defaultMessage: 'Load More',
  },
  singleResult: {
    id: 'resultsMessage.singleResult',
    defaultMessage: '1 Result',
  },
  singleResultWithNextLink: {
    id: 'resultsMessage.singResultWithNextLink',
    defaultMessage: 'Over 1 Result:',
  },
  multiResult: {
    id: 'resultsMessage.multiResult',
    defaultMessage: '{resultCount} Results',
  },
  multiResultWithNextLink: {
    id: 'resultsMessage.multiResultWithNextLink',
    defaultMessage: 'Over {resultCount} Results:',
  },
});

export const useStyles = makeStyles((_theme: Theme) => ({
  text: {
    display: 'inline-block',
  },
  button: {
    display: 'inline-block',
  },
}));

export type ResultsMessageClasses = GetClasses<typeof useStyles>;

export interface ResultsMessageOwnProps {
  isFetching: boolean;
  searchResult?: Omit<SingleFhirSearchResult, 'resourceType'>;
  hasMoreToLoad: boolean;
  loadMore: () => Promise<void>;
}

export type ResultsMessageProps = ResultsMessageOwnProps;

const ResultsMessage = (props: ResultsMessageProps) => {
  const { isFetching, searchResult, hasMoreToLoad, loadMore } = props;

  const classes = useStyles(props);
  const [firstIn, secondIn, onFirstExited, onSecondExited] =
    useChainedTransition(!isFetching);
  const formatMessage = useIntl().formatMessage;

  const dataIsPaginated = !!searchResult?.nextLink;
  const only1Result = searchResult?.results.length === 1;

  const loadingMessage = formatMessage(messages.loadingData);

  let resultMessage: string;

  if (!dataIsPaginated && only1Result) {
    resultMessage = formatMessage(messages.singleResult);
  } else if (dataIsPaginated && only1Result) {
    resultMessage = formatMessage(messages.singleResultWithNextLink);
  } else if (!dataIsPaginated && !only1Result) {
    resultMessage = formatMessage(messages.multiResult, {
      resultCount: searchResult?.results.length,
    });
  } else {
    resultMessage = formatMessage(messages.multiResultWithNextLink, {
      resultCount: searchResult.results.length,
    });
  }

  const renderText = (text: string) => (
    <Text className={classes.text} size="caption">
      <Pill>{text}</Pill>
    </Text>
  );

  return (
    <Box paddingLeft={2} paddingRight={1}>
      <Fade unmountOnExit onExited={onFirstExited} in={firstIn}>
        <Box align="center" gap={1}>
          {hasMoreToLoad && (
            <Button
              className={classes.button}
              variant="text"
              onClick={loadMore}
            >
              {formatMessage(messages.loadMore)}
            </Button>
          )}
          {renderText(resultMessage)}
        </Box>
      </Fade>
      <Fade unmountOnExit onExited={onSecondExited} in={secondIn}>
        {renderText(loadingMessage)}
      </Fade>
    </Box>
  );
};

export default React.memo(ResultsMessage);
